import React from "react";
import { Phone, Mail, MapPin, Clock, Navigation, Facebook, Instagram, Youtube } from "lucide-react";
import { WhatsAppIcon, TikTokIcon } from "@/components/SocialIcons";
import { CONTACT, SOCIALS } from "@/config";
import { useI18n } from "@/i18n";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram, youtube: Youtube, tiktok: TikTokIcon, whatsapp: WhatsAppIcon };

export const Contact = () => {
  const { t } = useI18n();
  return (
    <section id="contact" className="py-24 bg-white" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand mb-3">{t.contact.eyebrow}</p>
          <h2 className="font-head text-3xl sm:text-4xl font-semibold text-navy tracking-tight">{t.contact.title}</h2>
          <p className="mt-4 text-slate-600">{t.contact.subtitle}</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 p-5 border border-navy/10 rounded-md hover:border-brand/40 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-md bg-navy/5 group-hover:bg-brand/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-navy group-hover:text-brand" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.contact.phone}</p>
                <p className="font-semibold text-navy">{CONTACT.phone}</p>
              </div>
            </a>

            <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 p-5 border border-navy/10 rounded-md hover:border-brand/40 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-md bg-navy/5 group-hover:bg-brand/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-navy group-hover:text-brand" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.contact.email}</p>
                <p className="font-semibold text-navy">{CONTACT.email}</p>
              </div>
            </a>

            <a href={CONTACT.whatsappHref} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp" className="flex items-center gap-4 p-5 border border-navy/10 rounded-md hover:border-brand/40 hover:shadow-md transition-all group">
              <div className="w-12 h-12 rounded-md bg-[#25D366]/10 flex items-center justify-center">
                <WhatsAppIcon className="w-5 h-5 text-[#25D366]" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.contact.whatsapp}</p>
                <p className="font-semibold text-navy">{CONTACT.phone}</p>
              </div>
            </a>

            <div data-testid="contact-address" className="flex items-start gap-4 p-5 border border-navy/10 rounded-md">
              <div className="w-12 h-12 rounded-md bg-navy/5 flex items-center justify-center shrink-0">
                <MapPin className="w-5 h-5 text-navy" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">{t.contact.address}</p>
                <p className="font-semibold text-navy">{CONTACT.addressLine1}</p>
                <p className="text-slate-600">{CONTACT.addressLine2}</p>
              </div>
            </div>

            <div data-testid="contact-hours" className="flex items-start gap-4 p-5 border border-navy/10 rounded-md">
              <div className="w-12 h-12 rounded-md bg-navy/5 flex items-center justify-center shrink-0">
                <Clock className="w-5 h-5 text-navy" />
              </div>
              <div className="w-full">
                <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-1">{t.contact.hours}</p>
                <p className="text-sm text-slate-600 leading-relaxed">{t.contact.hoursNote}</p>
              </div>
            </div>

            <a
              data-testid="contact-directions-btn"
              href={CONTACT.directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-lift inline-flex items-center justify-center gap-2 w-full bg-brand hover:bg-brand-hover text-white font-semibold px-6 py-3.5 rounded-md shadow-md shadow-brand/20"
            >
              <Navigation className="w-4 h-4" /> {t.contact.directions}
            </a>

            <div data-testid="contact-socials" className="pt-2">
              <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold mb-3">{t.contact.follow}</p>
              <div className="flex gap-3">
                {SOCIALS.filter((s) => s.icon !== "whatsapp").map((s) => {
                  const Icon = SOCIAL_ICONS[s.icon];
                  return (
                    <a
                      key={s.name}
                      href={s.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={s.name}
                      data-testid={`contact-social-${s.icon}`}
                      className="w-11 h-11 rounded-md border border-navy/10 bg-white text-navy hover:bg-navy hover:text-white hover:border-navy flex items-center justify-center transition-colors btn-lift"
                    >
                      <Icon className="w-5 h-5" />
                    </a>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="rounded-md overflow-hidden border border-navy/10 min-h-[400px]" data-testid="contact-map">
            <iframe
              title={t.contact.mapTitle}
              src={`https://www.google.com/maps?q=${encodeURIComponent(CONTACT.mapsQuery)}&output=embed`}
              className="w-full h-full min-h-[400px]"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </section>
  );
};
